'use client'
import { useCallback, useState } from 'react'
import { FieldValues, Path, UseFormGetValues, UseFormSetError } from 'react-hook-form'
import { toast } from 'react-toastify'
import { IApiErrorDetail, isApiError } from '~/schemas/errorSchemas'

interface IFormActionOptions<TFormValues extends FieldValues, TResponse> {
    /** O `setError` do `useForm`, para mostrar o erro de cada campo. */
    setError: UseFormSetError<TFormValues>
    /**
     * O `getValues` do `useForm`: o hook só leva ao formulário o erro de um campo que o formulário tem (com valor
     * inicial no `defaultValues` ou registrado). O de outro campo iria para um lugar que não aparece na tela.
     */
    getValues: UseFormGetValues<TFormValues>
    onSuccess?: (response: TResponse) => void
    /** Erro que não tem onde aparecer no formulário (padrão: toast). */
    onGenericError?: (message: string) => void
    /**
     * Campo da API -> campo do formulário, quando os nomes diferem (ex.: `{ MainDocNumber: 'cpf' }`). O campo do mapa
     * vai sempre para o formulário; fora do mapa, o campo da API vai com a primeira letra minúscula (`Email` -> `email`)
     * se o formulário tiver esse campo.
     */
    fieldMap?: Partial<Record<string, Path<TFormValues>>>
}

const GENERIC_MESSAGE = 'Ocorreu um erro ao processar sua solicitação.'
const UNEXPECTED_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente.'

const lowerFirst = (field: string) => field.charAt(0).toLowerCase() + field.slice(1)

/** `true` quando o caminho (`email`, `address.zipCode`) existe nos valores do formulário. */
function hasPath(values: unknown, path: string): boolean {
    let current = values
    for (const key of path.split('.')) {
        if (!current || typeof current !== 'object' || !Object.hasOwn(current, key)) return false
        current = (current as Record<string, unknown>)[key]
    }
    return true
}

/**
 * Orquestra o envio de um formulário (padrão do orbit): `isLoading`, `onSuccess` e o feedback do erro.
 *
 * Contrato com o service (`apiService(...).onError(..., { useDefaultErrorHandler: false })`): o service usado com este
 * hook desliga o toast padrão do ApiService e só lança o `ApiError`; quem mostra o erro é o hook, uma vez só:
 * - erro de um campo que o formulário tem (pelo `fieldMap` ou pelo nome com a primeira letra minúscula) aparece no
 *   campo, com `setError`;
 * - o resto (erro sem campo ou de um campo que o formulário não tem) vai junto num toast (ou no `onGenericError`);
 * - sem nenhum erro detalhado, o toast mostra a `message` do `ApiError`.
 * Nunca os dois para o mesmo erro. Com o toast padrão ligado no service, o erro apareceria duas vezes.
 */
export function useFormAction<TFormValues extends FieldValues, TResponse>(
    action: (data: TFormValues) => Promise<TResponse>,
    options: IFormActionOptions<TFormValues, TResponse>,
) {
    const [isLoading, setIsLoading] = useState(false)
    const { setError, getValues, onSuccess, onGenericError, fieldMap } = options

    const handler = useCallback(
        async (data: TFormValues) => {
            const showGeneric = (message: string) => (onGenericError ? onGenericError(message) : toast.error(message))

            /** Campo do formulário que recebe o erro, ou `undefined` se o formulário não tem onde mostrá-lo. */
            const formField = (apiField: string): Path<TFormValues> | undefined => {
                const mapped = fieldMap?.[apiField]
                if (mapped) return mapped
                const field = lowerFirst(apiField)
                return hasPath(getValues(), field) ? (field as Path<TFormValues>) : undefined
            }

            setIsLoading(true)
            try {
                const response = await action(data)
                onSuccess?.(response)
            } catch (error) {
                if (!isApiError(error)) {
                    console.error('useFormAction capturou um erro que não é ApiError:', error)
                    showGeneric(UNEXPECTED_MESSAGE)
                    return
                }

                const leftover: IApiErrorDetail[] = []
                for (const detail of error.errors) {
                    const field = detail.field ? formField(detail.field) : undefined
                    if (field) setError(field, { type: 'server', message: detail.message })
                    else leftover.push(detail)
                }

                if (leftover.length > 0) showGeneric(leftover.map((detail) => detail.message).join('\n'))
                else if (error.errors.length === 0) showGeneric(error.message || GENERIC_MESSAGE)
            } finally {
                setIsLoading(false)
            }
        },
        [action, setError, getValues, onSuccess, onGenericError, fieldMap],
    )

    return { handler, isLoading }
}
